import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import dropsLoader from '@/mixins/components/drops-loader';
import { guid } from '@/mixins/guid';
import tooltip from '@/mixins/components/tooltip';
import countries from '@/payloads/shipping-countries.json';

export default Vue.component('store-manage-order', {
  template: require('./template.html'),
  name: 'store-manage-order',
  mixins: [guid],
  computed: {
    ...mapGetters(['currentSite', 'storeOrders']),
    countries() {
      return countries;
    },
    states() {
      const country = this.countries[this.shippingAddress.country];
      const noRegion = { name: 'N/A', iso: 'N/A' };
      const states = country && country.regions ? country.regions : [noRegion];
      return states.length > 0 ? states : [noRegion];
    },
    displayAddress() {
      const order = this.activeOrder;
      const apt = order.address.apt !== '' ? ` ${order.address.apt}, ` : ' ';
      return `${order.address.address},${apt}${order.address.city}, ${order.address.state} ${order.address.zipCode} ${order.address.country}`;
    },
    refundWarnings() {
      const warning = `Changing this order to a "${this.status}" status will attempt a full refund to the customer if it has not already been issued.`;
      return {
        cancelled: warning,
        'refunded & no return': warning,
        'refunded & returned': warning,
      };
    },
  },
  components: {
    dropsLoader,
    tooltip,
  },
  data() {
    return {
      editstatus: false,
      editorderNotes: false,
      editemail: false,
      editShippingAddress: false,
      edittrackingNumber: false,
      trackingNumberError: false,
      loading: false,
      loaders: {
        status: false,
        orderNotes: false,
        email: false,
        statusCheck: false,
        shippingAddress: false,
        trackingNumber: false,
      },
      trackingNumber: false,
      trackingNumberLink: false,
      orderNotes: false,
      email: false,
      status: false,
      shippingAddress: {
        address: '',
        apt: '',
        city: '',
        country: '',
        state: '',
        zipCode: '',
      },
      initials: {
        orderNotes: false,
        email: false,
        status: false,
        trackingNumber: false,
        trackingNumberLink: false,
        shippingAddress: {
          address: '',
          apt: '',
          city: '',
          country: '',
          state: '',
          zipCode: '',
        },
      },
      statusReason: '',
      confirmStatus: false,
      confirmDelete: false,
      confirmAddress: false,
      validateList: ['address', 'city', 'country', 'state', 'zipCode'],
      emailStatusChange: true,
    };
  },
  props: ['activeOrder', 'statuses'],
  created() {
    this.setInitialValues();
    this.$log.debug('order: ', this.activeOrder);
  },
  methods: {
    setInitialValues() {
      this.trackingNumber = this.activeOrder.trackingNumber || '';
      this.trackingNumberLink = this.activeOrder.trackingNumberLink || '';
      this.orderNotes = this.activeOrder.orderNotes || '';
      this.email = this.activeOrder.email || '';
      this.status = this.activeOrder.status;
      this.shippingAddress.address = this.activeOrder.address.address;
      this.shippingAddress.apt = this.activeOrder.address.apt;
      this.shippingAddress.city = this.activeOrder.address.city;
      this.shippingAddress.country = this.activeOrder.address.country;
      this.shippingAddress.state = this.activeOrder.address.state;
      this.shippingAddress.zipCode = this.activeOrder.address.zipCode;
      this.initials.orderNotes = this.orderNotes;
      this.initials.email = this.email;
      this.initials.trackingNumber = this.trackingNumber;
      this.initials.trackingNumberLink = this.trackingNumberLink;
      this.initials.status = this.status;
      this.initials.shippingAddress.address = this.shippingAddress.address;
      this.initials.shippingAddress.apt = this.shippingAddress.apt;
      this.initials.shippingAddress.city = this.shippingAddress.city;
      this.initials.shippingAddress.country = this.shippingAddress.country;
      this.initials.shippingAddress.state = this.shippingAddress.state;
      this.initials.shippingAddress.zipCode = this.shippingAddress.zipCode;
    },
    async updateOrder(key) {
      if (this.hasChanges(key)) {
        this.trackingNumberError = false;
        this.loaders[key] = true;
        try {
          const isStatus = key === 'status';
          if (isStatus
            && this.status === 'shipped'
            && !this.activeOrder.trackingNumber) {
            this.trackingNumberError = 'A tracking number must be present when changing order status to shipped.';
            return;
          }
          await this.$lightning([{
            name: 'updateOrder',
            payload: {
              siteId: this.currentSite._id,
              orderId: this.activeOrder._id,
              attr: key,
              value: this[key],
              sendEmail: isStatus && this.emailStatusChange,
              statusReason: this.statusReason,
            },
          }]);
          this.$store.dispatch('setGlobalValue', {
            item: this.activeOrder,
            key,
            value: this[key],
          });
          if (isStatus) {
            this.confirmStatus = false;
          }
          this[`edit${key}`] = false;
          this.initials[key] = this[key];
          this.$store.dispatch('addNotification', {
            id: this.guid(),
            type: 'success',
            message: `${this._startCase(key)} saved!`,
          });
        } catch (e) {
          const message = e.message || e;
          this.$log.error(message);
          this.$store.dispatch('addNotification', {
            id: this.guid(),
            type: 'error',
            message,
          });
        } finally {
          this.loaders[key] = false;
        }
      }
    },
    async updateTrackingLink() {
      if (this.hasChanges('trackingNumber') || this.hasChanges('trackingNumberLink')) {
        this.trackingNumberError = false;
        this.loaders.trackingNumber = true;
        try {
          await this.$lightning([{
            name: 'updateOrder',
            payload: {
              siteId: this.currentSite._id,
              orderId: this.activeOrder._id,
              attr: 'trackingNumber',
              value: this.trackingNumber,
            },
          }]);
          await this.$lightning([{
            name: 'updateOrder',
            payload: {
              siteId: this.currentSite._id,
              orderId: this.activeOrder._id,
              attr: 'trackingNumberLink',
              value: this.trackingNumberLink,
            },
          }]);
          this.$store.dispatch('setGlobalValue', {
            item: this.activeOrder,
            key: 'trackingNumber',
            value: this.trackingNumber,
          });
          this.$store.dispatch('setGlobalValue', {
            item: this.activeOrder,
            key: 'trackingNumberLink',
            value: this.trackingNumberLink,
          });
          this.edittrackingNumber = false;
          this.initials.trackingNumber = this.trackingNumber;
          this.$store.dispatch('addNotification', {
            id: this.guid(),
            type: 'success',
            message: 'Tracking Number saved!',
          });
        } catch (e) {
          const message = e.message || e;
          this.$log.error(message);
          this.$store.dispatch('addNotification', {
            id: this.guid(),
            type: 'error',
            message,
          });
        } finally {
          this.loaders.trackingNumber = false;
        }
      }
    },
    hasChanges(type) {
      return !_.isEqual(this[type], this.initials[type]);
    },
    topCancel() {
      this.editShippingAddress = !this.editShippingAddress;
      if (!this.editShippingAddress) {
        this.cancelChanges('shippingAddress');
        this.confirmAddress = false;
      }
    },
    cancelChanges(type) {
      this.$set(this, type, _.cloneDeep(this.initials[type]));
    },
    cancelTracking() {
      if (this.hasChanges('trackingNumber') || this.hasChanges('trackingNumberLink')) {
        this.cancelChanges('trackingNumber');
        this.cancelChanges('trackingNumberLink');
        this.edittrackingNumber = false;
      }
    },
    totalFormat(price) {
      const currency = this.activeOrder.pricing.type.toUpperCase();
      return `${parseFloat(price).toFixed(2)} ${currency}`;
    },
    getSubtotal(item) {
      const price = parseInt(this.activeOrder.mappedProducts[item._id].price, 10);
      const count = parseInt(item.count, 10);
      const currency = this.activeOrder.pricing.type.toUpperCase();
      return `${parseFloat(price * count).toFixed(2)} ${currency}`;
    },
    getOptions(item) {
      const selectedValues = [];
      const product = this.activeOrder.mappedProducts[item._id];
      product.options.forEach((option) => {
        const val = item.selectedOptions[option._id];
        if (typeof val === 'undefined') {
          selectedValues.push(`${option.label}: ${option.items[0].value}`);
        } else {
          selectedValues.push(`${option.label}: ${val}`);
        }
      });
      return selectedValues.length > 0 ? `<strong>Options</strong>: ${selectedValues.join(' | ')}` : '';
    },
    async deleteOrder() {
      this.loading = true;
      this.confirmDelete = false;
      const notif = {
        id: this.guid(),
        type: 'success',
        message: 'Order successfully deleted!',
      };
      try {
        await this.$lightning([{
          name: 'deleteOrder',
          payload: {
            siteId: this.currentSite._id,
            orderId: this.activeOrder._id,
          },
        }]);
        const index = _.findIndex(this.storeOrders, o => o._id === this.activeOrder._id);
        if (index >= 0) {
          this.$store.dispatch('spliceDropletVal', {
            array: this.storeOrders,
            index,
          });
        }
        this.$emit('view-order', null);
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        notif.type = 'error';
        notif.message = message;
      } finally {
        this.loading = false;
        this.$store.dispatch('addNotification', notif);
      }
    },
    async checkPaymentStatus() {
      this.loaders.statusCheck = true;
      try {
        const payment = await this.$lightning([{
          name: 'getPaymentIntentStatus',
          payload: {
            siteId: this.currentSite._id,
            orderId: this.activeOrder._id,
          },
        }]);
        if (this.activeOrder.payment.status !== payment.status) {
          this.$store.dispatch('setGlobalValue', {
            item: this.activeOrder.payment,
            key: 'status',
            value: payment.status,
          });
        }
      } catch (e) {
        this.$log.error(e.message || e);
      } finally {
        this.loaders.statusCheck = false;
      }
    },
    async saveAddress() {
      const passed = this.validateCustom(this.validateList);
      const hasChanges = this.hasChanges('shippingAddress');
      if (passed && hasChanges) {
        this.loaders.shippingAddress = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Shipping address successfully updated!',
        };
        try {
          const payload = {
            siteId: this.currentSite._id,
            orderId: this.activeOrder._id,
            shippingAddress: this.shippingAddress,
          };
          const { order } = await this.$lightning([{ name: 'updateShippingAddress', payload }]);
          const item = this.activeOrder;
          _.forOwn(order, (value, key) => {
            this.$store.dispatch('setGlobalValue', { item, key, value });
          });
          this.editShippingAddress = false;
          this.confirmAddress = false;
          this.$set(this.initials, 'shippingAddress', _.cloneDeep(this.shippingAddress));
        } catch (e) {
          const message = e.message || e;
          this.$log.error(message);
          notif.type = 'error';
          notif.message = message;
        } finally {
          this.loaders.shippingAddress = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    validateCustom(list) {
      const errors = list.filter(input => this.hasErrors(input));
      return errors.length < 1;
    },
    hasErrors(type) {
      const input = this.shippingAddress[type].replace(/\s/g, '');
      return input === '';
    },
    changeCountry() {
      this.shippingAddress.state = this.states[0].iso;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/store/orders/manage-order/index.js